import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchEventById, updateEvent } from "../../services/eventService";
import TextEditor from "../../components/TextEditor";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { message } from "antd";
import { jwtDecode } from "jwt-decode";
import { fetchUserById } from "../../services/userService";
import { BASE_URL_IMAGE } from "../../config/api";

const EditEvent = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    startDate: "",
    endDate: "",
    conductedBy: "",
    eventMode: "",
    redirectUrl: "",
    eventTags: "",
    metaTags: "",
    description: "",
    isFeatured: 0,
    isSingleDay: 0
  });
  
  const [bannerImage, setBannerImage] = useState(null);
  const [previewBanner, setPreviewBanner] = useState("");
  const [thumbnailImage, setThumbnailImage] = useState(null);
  const [previewThumbnail, setPreviewThumbnail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loggedInUserData, setLoggedInUserData] = useState(null);

  // Fetch logged-in user for logging
  useEffect(() => {
    const fetchAndSetUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const user = await fetchUserById(decoded.id);
          setLoggedInUserData(user);
        } catch (error) {
          console.error("Error decoding token or fetching user:", error);
        }
      }
    };
    fetchAndSetUser();
  }, []);

  // Fetch event data on component mount
  useEffect(() => {
    const loadEventData = async () => {
      try {
        const event = await fetchEventById(id);
      
        // Format dates for the date input (YYYY-MM-DD)
        const formatDateForInput = (dateString) => {
          if (!dateString) return "";
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        setFormData({
          title: event.event_title || "",
          startDate: formatDateForInput(event.event_startDate),
          endDate: formatDateForInput(event.event_endDate),
          conductedBy: event.event_conductedBy || "",
          eventMode: event.event_mode || "",
          redirectUrl: event.event_url || "",
          eventTags: event.event_tags || "",
          metaTags: event.event_metaTags || "",
          description: event.event_content || "",
          isFeatured: event.event_isFeatured || 0,
          isSingleDay: event.event_isSingleDay || 0
        });

        if (event.event_img) {
          setPreviewBanner(`${BASE_URL_IMAGE}/uploads/eventImages/${event.event_img}`);
        }
        if (event.event_thumbnail) {
          setPreviewThumbnail(`${BASE_URL_IMAGE}/uploads/eventImages/${event.event_thumbnail}`);
        }
        
      } catch (error) {
        message.error("Failed to load event data");
      } finally {
        setIsLoading(false);
      }
    };

    loadEventData();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerImage(file);
      setPreviewBanner(URL.createObjectURL(file));
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailImage(file);
      setPreviewThumbnail(URL.createObjectURL(file));
    }
  };

  const toggleFeatured = () => {
    setFormData(prev => ({ ...prev, isFeatured: prev.isFeatured ? 0 : 1 }));
  };

  const toggleSingleDay = () => {
    setFormData(prev => ({ ...prev, isSingleDay: prev.isSingleDay ? 0 : 1 }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Log update attempt
    const logData = {
      action: 'EVENT_UPDATE_ATTEMPT',
      user: loggedInUserData?.user_email || 'unknown',
      timestamp: new Date().toISOString(),
      eventId: id,
      data: {
        title: formData.title,
        isFeatured: formData.isFeatured,
        isSingleDay: formData.isSingleDay
      }
    };

    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title);
    formDataToSend.append("content", formData.description);
    formDataToSend.append("isFeatured", formData.isFeatured);
    formDataToSend.append("isSingleDay", formData.isSingleDay);
    formDataToSend.append("startDate", formData.startDate);
    formDataToSend.append("endDate", formData.endDate);
    formDataToSend.append("conductedBy", formData.conductedBy);
    formDataToSend.append("mode", formData.eventMode);
    formDataToSend.append("url", formData.redirectUrl);
    formDataToSend.append("tags", formData.eventTags);
    formDataToSend.append("metaTags", formData.metaTags);
    
    if (bannerImage) {
      formDataToSend.append("event_image", bannerImage);
    }
    if (thumbnailImage) {
      formDataToSend.append("event_thumbnail", thumbnailImage);
    }

    // Add user email for backend logging
    if (loggedInUserData?.user_email) {
      formDataToSend.append("updatedbyemail", loggedInUserData.user_email);
    }

    try {
      await updateEvent(id, formDataToSend);
      
      // Log successful update
      const successLog = {
        action: 'EVENT_UPDATE_SUCCESS',
        user: loggedInUserData?.user_email || 'unknown',
        timestamp: new Date().toISOString(),
        eventId: id,
        title: formData.title
      };

      message.success("Event updated successfully!");
      navigate("/list-events");
    } catch (error) {
      // Log update failure
      const errorLog = {
        action: 'EVENT_UPDATE_FAILED',
        user: loggedInUserData?.user_email || 'unknown',
        timestamp: new Date().toISOString(),
        eventId: id,
        error: error.message
      };

      message.error(error.message || "Failed to update event");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="text-center p-5">Loading event data...</div>;
  }

  return (
    <div className="main-wrapper">
      <Header />
      <Sidebar id="menu-item11" id1="menu-items11" activeClassName="edit-blog" />

      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="#">Events</Link></li>
                  <li className="breadcrumb-item"><FeatherIcon icon="chevron-right" /></li>
                  <li className="breadcrumb-item active">Edit Event</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit} encType="multipart/form-data">
                    <div className="row">
                      <div className="col-12">
                        <div className="form-heading">
                          <h4>Event Details</h4>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>Event Title <span className="login-danger">*</span></label>
                          <input
                            className="form-control"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>Select Start Date <span className="login-danger">*</span></label>
                          <input
                            className="form-control"
                            type="date"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>Select End Date <span className="login-danger">*</span></label>
                          <input
                            className="form-control"
                            type="date"
                            name="endDate"
                            value={formData.endDate}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>Conducted By <span className="login-danger">*</span></label>
                          <input
                            className="form-control"
                            name="conductedBy"
                            value={formData.conductedBy}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>Select Event Mode <span className="login-danger">*</span></label>
                          <select
                            className="form-control"
                            name="eventMode"
                            value={formData.eventMode}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select Mode</option>
                            <option value="Online">Online</option>
                            <option value="Offline">Offline</option>
                            <option value="Hybrid">Hybrid</option>
                          </select>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>Redirect URL</label>
                          <input
                            className="form-control"
                            name="redirectUrl"
                            value={formData.redirectUrl}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>Event Tags</label>
                          <input
                            className="form-control"
                            name="eventTags"
                            value={formData.eventTags}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>Meta Tags</label>
                          <input
                            className="form-control"
                            name="metaTags"
                            value={formData.metaTags}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="isFeatured"
                            checked={!!formData.isFeatured}
                            onChange={toggleFeatured}
                          />
                          <label className="form-check-label" htmlFor="isFeatured">
                            Is Featured
                          </label>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="isSingleDay"
                            checked={!!formData.isSingleDay}
                            onChange={toggleSingleDay}
                          />
                          <label className="form-check-label" htmlFor="isSingleDay">
                            Is Single Day Event
                          </label>
                        </div>
                      </div>

                      <div className="col-12 mt-3">
                        <TextEditor
                          value={formData.description}
                          onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                        />
                      </div>

                      <div className="col-md-6 mt-3">
                        <div className="form-group local-forms">
                          <label>Current Event Image</label>
                          {previewBanner && (
                            <img
                              src={previewBanner}
                              alt="Current Banner"
                              className="img-thumbnail mb-2"
                              style={{ maxHeight: "150px" }}
                            />
                          )}
                          <input
                            type="file"
                            className="form-control"
                            onChange={handleBannerChange}
                            accept="image/*"
                          />
                        </div>
                      </div>
                      <div className="col-md-6 mt-3">
                        <div className="form-group local-forms">
                          <label>Current Event Thumbnail</label>
                          {previewThumbnail && (
                            <img
                              src={previewThumbnail}
                              alt="Current Thumbnail"
                              className="img-thumbnail mb-2"
                              style={{ maxHeight: "150px" }}
                            />
                          )}
                          <input
                            type="file"
                            className="form-control"
                            onChange={handleThumbnailChange}
                            accept="image/*"
                          />
                        </div>
                      </div>

                      <div className="col-12 mt-4 text-end">
                        <button
                          type="submit"
                          className="btn btn-primary me-2"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Updating..." : "Update Event"}
                        </button>
                        <Link to="/list-events" className="btn btn-secondary">
                          Cancel
                        </Link>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEvent;